import { useEffect, useState } from "react";
import { useOutletContext, useParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft,
  Zap,
  Users,
  Clock,
  Tag,
  TrendingUp,
} from "lucide-react";
import { LanguagePill, formatTimeLeft } from "@/components/TaskCard";
import EnterRoundDialog from "@/components/EnterRoundDialog";
import { getTask } from "@/lib/afritask";

const TYPE_LABELS = {
  voice_rating: "Voice rating",
  prompt_rating: "Prompt rating",
  image_labeling: "Image labeling",
  audio_transcription: "Audio transcription",
  translation: "Translation",
  text_classification: "Text classification",
};

export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshWallet } = useOutletContext();
  const [task, setTask] = useState(null);
  const [entering, setEntering] = useState(null);

  const load = async () => {
    try {
      const t = await getTask(id);
      setTask(t);
    } catch {
      setTask(null);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (task === null) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const filled = Math.min(
    100,
    Math.round((task.entered_count / task.applicant_cap) * 100)
  );
  const timeLeft = formatTimeLeft(task.deadline);
  const closingSoon = timeLeft.includes("m") || timeLeft === "closed";

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <Link
        to="/app/tasks"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
        data-testid="back-to-tasks"
      >
        <ArrowLeft className="h-4 w-4" /> Back to tasks
      </Link>

      {/* Header */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <LanguagePill language={task.language} />
          <Badge variant="outline" className="text-[10px]">
            <Tag className="mr-1 h-3 w-3" />
            {TYPE_LABELS[task.task_type] || task.task_type}
          </Badge>
          {task.status !== "open" && (
            <Badge className="bg-neutral-800 text-white hover:bg-neutral-800">
              Round closed
            </Badge>
          )}
          {task.has_entered && (
            <Badge className="bg-amber-700 hover:bg-amber-700">
              You&apos;re in
            </Badge>
          )}
        </div>
        <h1
          className="text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl"
          data-testid="task-title"
        >
          {task.title}
        </h1>
        <p className="max-w-2xl text-base leading-relaxed text-neutral-600">
          {task.description}
        </p>
      </div>

      {/* Stats */}
      <Card>
        <CardContent className="grid grid-cols-2 gap-4 p-6 sm:grid-cols-4">
          <Stat icon={Zap} label="Entry cost" value={`${task.entry_cost_points} pts`} tone="indigo" />
          <Stat
            icon={TrendingUp}
            label="Task reward"
            value={`₦${(((task.winner_reward_points ?? task.reward_points) || 0) * 10).toLocaleString()}`}
            tone="emerald"
          />
          <div>
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-neutral-500">
              <Users className="h-3.5 w-3.5" />
              Applicants
            </div>
            <p className="mt-1 text-2xl font-bold tabular-nums">
              {task.entered_count}
              <span className="text-base font-medium text-neutral-400">
                {" "}
                / {task.applicant_cap}
              </span>
            </p>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
              <div
                className="h-full rounded-full bg-amber-600 transition-all"
                style={{ width: `${filled}%` }}
              />
            </div>
          </div>
          <Stat
            icon={Clock}
            label="Deadline"
            value={timeLeft}
            tone={closingSoon ? "amber" : "neutral"}
          />
        </CardContent>
      </Card>

      {/* Sample */}
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-widest text-neutral-500">
          Sample brief
        </h3>
        <Card className="mt-2">
          <CardContent className="p-6">
            <p className="text-sm leading-relaxed text-neutral-700">
              {task.task_payload?.preview || task.description}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* CTA */}
      <div className="sticky bottom-4 sm:static">
        <Card className="border-emerald-200 bg-white shadow-lg sm:shadow-none">
          <CardContent className="flex flex-col items-stretch gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-neutral-500">
                Enter this round
              </p>
              <p className="text-lg font-semibold">
                Cost: <span className="text-neutral-900">{task.entry_cost_points} pts</span>{" "}
                · Payout: <span className="text-emerald-700">₦{(((task.winner_reward_points ?? task.reward_points) || 0) * 10).toLocaleString()} to the top {task.winners_count || "?"} scorers</span>
              </p>
            </div>
            <Button
              size="lg"
              disabled={task.has_entered || task.status !== "open"}
              onClick={() => setEntering(task)}
              className="rounded-lg bg-emerald-600 px-6 hover:bg-emerald-700"
              data-testid="task-detail-enter-btn"
            >
              {task.has_entered
                ? "Already entered"
                : task.status !== "open"
                  ? "Round closed"
                  : `Enter round for ${task.entry_cost_points} pts`}
            </Button>
          </CardContent>
        </Card>
      </div>

      <EnterRoundDialog
        task={entering}
        onClose={() => setEntering(null)}
        onEntered={async () => {
          await Promise.all([load(), refreshWallet()]);
          setTimeout(() => navigate("/app/applications"), 800);
        }}
      />
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone = "neutral" }) {
  const tones = {
    indigo: "text-amber-700",
    emerald: "text-emerald-600",
    amber: "text-amber-600",
    neutral: "text-neutral-500",
  };
  return (
    <div>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-neutral-500">
        <Icon className={`h-3.5 w-3.5 ${tones[tone]}`} />
        {label}
      </div>
      <p className="mt-1 text-2xl font-bold tabular-nums">{value}</p>
    </div>
  );
}
